import { Component, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ErrorInfo, ReactNode } from 'react';
import { Upload, Wallet, Wrench, RefreshCw, Trash2, PlusCircle, Inbox } from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { supabase } from '../../lib/supabase';
import { LiveLedgerTab } from './components/LiveLedgerTab';

import { PartsTrackerTab } from './components/PartsTrackerTab';
import { RecurringExpensesManager } from './RecurringExpensesManager';
import { ReceiptUploadModal } from './components/ReceiptUploadModal';
import { ManualExpenseModal } from './components/ManualExpenseModal';
import { ReviewQueueModal } from './components/ReviewQueueModal';
import { BinTab } from './components/BinTab';
import { ExpensesOverview } from './components/ExpensesOverview';

class ChartErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
    constructor(props: { children: ReactNode }) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error: Error) { return { error }; }
    componentDidCatch(error: Error, info: ErrorInfo) { console.error('ExpensesOverview crashed:', error, info); }
    render() {
        if (this.state.error) {
            return (
                <div className="rounded-2xl bg-card border border-line p-6 text-sm text-fg-3">
                    Chart unavailable — {this.state.error.message}
                </div>
            );
        }
        return this.props.children;
    }
}

export function ExpensesHub() {
    const { molded, activeOrgId } = useVertical();
    const [activeTab, setActiveTab] = useState<'expenses' | 'parts' | 'recurring' | 'bin'>('expenses');
    const [searchParams, setSearchParams] = useSearchParams();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0); // Forces LiveLedgerTab remount after upload
    const [reviewCount, setReviewCount] = useState(0); // Items waiting in the Review Queue

    // Keep the Review Queue button's count live. Same two sources the queue
    // drains: OCR uploads held as needs_review/manual_entry, plus pending
    // supplier-credit matches. Re-runs on refreshKey so resolving an item (which
    // bumps refreshKey via onResolved) updates the badge.
    useEffect(() => {
        if (!activeOrgId) return;
        let cancelled = false;
        const fetchReviewCount = async () => {
            const [queue, credits] = await Promise.all([
                supabase
                    .from('processing_queue')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .in('status', ['needs_review', 'manual_entry']),
                supabase
                    .from('ocr_review_queue')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .eq('status', 'pending'),
            ]);
            if (!cancelled) setReviewCount((queue.count ?? 0) + (credits.count ?? 0));
        };
        fetchReviewCount().catch((err) => console.error('ExpensesHub: review count failed', err));
        return () => {
            cancelled = true;
        };
    }, [activeOrgId, refreshKey]);

    // Deep-link: /expenses?review=1 (e.g. from the dashboard "Needs Attention"
    // card) opens the Review Queue straight away, then drops the param so a
    // refresh doesn't reopen it.
    useEffect(() => {
        if (searchParams.get('review') === '1') {
            setIsReviewModalOpen(true);
            searchParams.delete('review');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleUploadClick = () => {
        setIsUploadModalOpen(true);
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <ReceiptUploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSuccess={() => setRefreshKey(k => k + 1)}
                onOpenReview={() => setIsReviewModalOpen(true)}
            />
            <ManualExpenseModal
                isOpen={isManualModalOpen}
                onClose={() => setIsManualModalOpen(false)}
                onSuccess={() => setRefreshKey(k => k + 1)}
            />
            <ReviewQueueModal
                isOpen={isReviewModalOpen}
                onClose={() => setIsReviewModalOpen(false)}
                onResolved={() => setRefreshKey(k => k + 1)}
            />

            {/* Header & Global Action */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-line">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-gold-fill/10 rounded-xl border border-gold/20">
                        <Wallet className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-display font-bold text-fg tracking-tight">Expenses & Bills</h2>
                        <p className="text-fg-3 text-sm mt-1">Manage outflows, fixed costs, and material efficiency.</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        onClick={() => setIsReviewModalOpen(true)}
                        title={reviewCount > 0 ? `${reviewCount} item${reviewCount === 1 ? '' : 's'} need your review` : 'Review Queue'}
                        className={
                            reviewCount > 0
                                ? 'relative flex items-center justify-center gap-2 h-11 px-5 rounded-lg fd-btn-primary border border-gold font-bold font-display text-sm shadow-focus transition-all w-full md:w-auto animate-pulse'
                                : 'flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-card border border-line hover:bg-raised hover:border-gold/40 text-fg hover:text-gold font-semibold font-display text-sm transition-colors shadow-sm w-full md:w-auto'
                        }
                    >
                        <Inbox className="w-4 h-4" />
                        <span>{reviewCount > 0 ? `Review Now · ${reviewCount}` : 'Review Queue'}</span>
                        {reviewCount > 0 && (
                            <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-danger-fill text-on-fill text-[11px] font-bold font-mono shadow-md">
                                {reviewCount > 99 ? '99+' : reviewCount}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setIsManualModalOpen(true)}
                        className="flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-card border border-line hover:bg-raised hover:border-gold/40 text-fg hover:text-gold font-semibold font-display text-sm transition-colors shadow-sm w-full md:w-auto"
                    >
                        <PlusCircle className="w-4 h-4" />
                        <span>Add Manual Expense</span>
                    </button>
                    <button
                        onClick={handleUploadClick}
                        className="flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-accent/10 border border-accent/30 hover:bg-accent/20 hover:border-accent/50 text-accent font-semibold font-display text-sm transition-colors shadow-sm w-full md:w-auto"
                    >
                        <Upload className="w-4 h-4" />
                        <span>Upload Invoice</span>
                    </button>
                </div>
            </div>

            {/* Overview — KPI strip + HMRC donut + 6-month trend.
                Lives above the tabs so accountants and mechanics both see the
                shape of spending before drilling into the line-item table. */}
            <ChartErrorBoundary>
                <ExpensesOverview key={refreshKey} />
            </ChartErrorBoundary>

            {/* Navigation Tabs */}
            <div className="flex bg-card border border-line rounded-lg p-1 w-full md:w-auto overflow-x-auto gap-1">
                <button
                    onClick={() => setActiveTab('expenses')}
                    className={`flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap min-w-[120px] flex-1 md:flex-none ${activeTab === 'expenses' ? 'bg-gold-fill/10 text-gold shadow-sheen border border-gold/20' : 'text-fg-3 hover:text-fg hover:bg-raised'}`}
                >
                    <Wallet className="w-4 h-4" />
                    <span>Live Ledger</span>
                </button>
                <button
                    onClick={() => setActiveTab('recurring')}
                    className={`flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap min-w-[120px] flex-1 md:flex-none ${activeTab === 'recurring' ? 'bg-gold-fill/10 text-gold shadow-sheen border border-gold/20' : 'text-fg-3 hover:text-fg hover:bg-raised'}`}
                >
                    <RefreshCw className="w-4 h-4" />
                    <span>Recurring</span>
                </button>
                <button
                    onClick={() => setActiveTab('parts')}
                    className={`flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap min-w-[120px] flex-1 md:flex-none ${activeTab === 'parts' ? 'bg-gold-fill/10 text-gold shadow-sheen border border-gold/20' : 'text-fg-3 hover:text-fg hover:bg-raised'}`}
                >
                    <Wrench className="w-4 h-4" />
                    <span>{molded.jargon.item_label} Tracker</span>
                </button>
                <div className="w-[1px] h-6 bg-line mx-1 self-center hidden md:block" />
                <button
                    onClick={() => setActiveTab('bin')}
                    className={`flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap min-w-[100px] flex-1 md:flex-none ${activeTab === 'bin' ? 'bg-danger/10 text-danger border border-danger/20 shadow-sheen' : 'text-fg-3 hover:text-danger hover:bg-danger/5'}`}
                >
                    <Trash2 className="w-4 h-4" />
                    <span>Bin</span>
                </button>
            </div>

            {/* Module Content Slot */}
            <div className="min-h-[500px]">
                {activeTab === 'expenses' && <LiveLedgerTab key={refreshKey} />}
                {activeTab === 'recurring' && <RecurringExpensesManager />}
                {activeTab === 'parts' && <PartsTrackerTab />}
                {activeTab === 'bin' && <BinTab />}
            </div>
        </div>
    );
}

